import Cookies from "js-cookie";

// The Level5 apps a user moves between, and which they opened last. The
// history lives in one cookie on the parent domain (l5flt.com,
// l5fleet.com, level5fleet.com), so every app reads and writes the same
// list. Each app records its own address when it opens, so no app needs the
// others' addresses configured. This file is the same in every app; only
// THIS_APP differs.

export type AppKey =
	| "admiral"
	| "account"
	| "security"
	| "fleet"
	| "geofences"
	| "metrics"
	| "wms"
	| "resolve"
	| "trailerload"
	| "fleetai";

export const THIS_APP: AppKey = "trailerload";

// Names and icons as on Admiral's home page (Admiral is "Home").
export const APPS: Record<AppKey, { name: string; icon: string }> = {
	admiral: { name: "Home", icon: "/images/apps/admiral.svg" },
	account: { name: "Account", icon: "/images/apps/account.svg" },
	security: { name: "Enforce", icon: "/images/apps/security.svg" },
	fleet: { name: "Fleet", icon: "/images/apps/fleet.svg" },
	geofences: { name: "Virtual Yards", icon: "/images/apps/geofences.svg" },
	metrics: { name: "Metrics & Reports", icon: "/images/apps/metrics.svg" },
	wms: { name: "Dock Management", icon: "/images/apps/wms.svg" },
	resolve: { name: "Resolve", icon: "/images/apps/resolve.svg" },
	trailerload: { name: "Load Management", icon: "/images/apps/trailerload.svg" },
	fleetai: { name: "Fleet AI", icon: "/images/apps/fleetai.svg" },
};

export type AppLink = { key: AppKey; url: string };

const COOKIE = "l5_recentApps";
const MAX = 8;

// The parent domain the apps share; none on localhost (host-only cookie).
const cookieDomain = () => {
	const host = window.location.hostname;
	if (host === "localhost" || /^[\d.]+$/.test(host)) return undefined;
	return host.split(".").slice(-2).join(".");
};

const isAppKey = (k: unknown): k is AppKey => typeof k === "string" && k in APPS;

/** The apps most recently opened, newest first. */
export const recentApps = (): AppLink[] => {
	try {
		const list = JSON.parse(Cookies.get(COOKIE) || "[]");
		return Array.isArray(list)
			? list.filter((a) => isAppKey(a?.key) && typeof a?.url === "string" && /^https?:\/\//.test(a.url))
			: [];
	} catch {
		return [];
	}
};

/** Puts this app (at its own address) at the front of the history. */
export const recordAppVisit = (key: AppKey = THIS_APP, url = window.location.origin) => {
	const list = [{ key, url }, ...recentApps().filter((a) => a.key !== key)].slice(0, MAX);
	Cookies.set(COOKIE, JSON.stringify(list), { domain: cookieDomain(), expires: 365, sameSite: "lax" });
};

const configured = (key: AppKey, url: string | undefined): AppLink[] =>
	url && url !== "undefined" ? [{ key, url }] : [];

// With no history, in this order (an app never shows itself).
const DEFAULTS = (): AppLink[] => [
	...configured("fleet", process.env.REACT_APP_FLEET_APP),
	...configured("security", process.env.REACT_APP_SECURITY_APP),
	...configured("account", process.env.REACT_APP_ACCOUNT_APP),
];

/**
 * The sidebar's shortcuts: Home first, then the two apps opened most
 * recently other than this one, filled from Fleet, Security and Account.
 */
export const shortcutApps = (): AppLink[] => {
	const home = configured("admiral", process.env.REACT_APP_ADMIRAL_APP);
	const others: AppLink[] = [];
	for (const app of [...recentApps(), ...DEFAULTS()]) {
		if (app.key === THIS_APP || app.key === "admiral" || others.some((o) => o.key === app.key)) continue;
		others.push(app);
		if (others.length === 2) break;
	}
	return [...home, ...others];
};
